import { useState, useEffect } from 'react';
import { cn, parseTimestamp } from '@client/lib/utils';
import { NavigationBar } from '@client/components/layout';
import { IOSToggle } from '@client/components/ui';
import { LoadingSpinner } from '@client/components/common';
import { 
  adminLogout, 
  getAnalytics, 
  getSettings, 
  updateSettings,
  getAdminTasks,
  updateAdminTask,
  createAdminTask,
  deleteAdminTask,
  getAdminPhotos,
  deleteAdminPhoto,
  getAdminUsers,
  deleteAdminUser,
} from '@client/lib/api';
import type { Analytics, SiteSettings, TaskWithCreator, AdminPhoto, User, OnlineUser } from '@shared/types';
import { Trash2, Plus, Circle, Pencil, Check, X } from 'lucide-react';

interface AdminDashboardProps {
  onLogout: () => void;
}

export function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'photos' | 'tasks' | 'users'>('overview');
  const [loading, setLoading] = useState(true);
  const [savingDisabledMessage, setSavingDisabledMessage] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsData, settingsData] = await Promise.all([
        getAnalytics(),
        getSettings(),
      ]);
      setAnalytics(analyticsData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSite = async () => {
    if (!settings) return;
    try {
      await updateSettings({ siteEnabled: !settings.siteEnabled });
      setSettings({ ...settings, siteEnabled: !settings.siteEnabled });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleDisabledMessageChange = (disabledMessage: string) => {
    if (!settings) return;
    setSettings({ ...settings, disabledMessage });
  };

  const handleSaveDisabledMessage = async () => {
    if (!settings) return;

    setSavingDisabledMessage(true);
    try {
      await updateSettings({ disabledMessage: settings.disabledMessage });
    } catch (error) {
      console.error('Error updating disabled message:', error);
    } finally {
      setSavingDisabledMessage(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      onLogout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-ios-grouped-background)] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-ios-grouped-background)] pb-safe">
      <NavigationBar
        title="Admin"
        rightAction={
          <button onClick={handleLogout} className="text-primary text-[17px]">
            Logout
          </button>
        }
      />

      {/* Site Toggle */}
      <div className="mx-4 mt-4">
        <div className="bg-card rounded-[10px] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[17px] leading-[22px] font-medium">Site Active</p>
              <p className="text-[12px] leading-[16px] text-muted-foreground">
                {settings?.siteEnabled ? 'Guests can access the app' : 'Site is disabled'}
              </p>
            </div>
            <IOSToggle
              checked={settings?.siteEnabled ?? false}
              onChange={handleToggleSite}
            />
          </div>

          <div className="mt-4">
            <label className="block text-[13px] leading-[18px] font-medium text-muted-foreground mb-2">
              Disabled Message
            </label>
            <textarea
              value={settings?.disabledMessage ?? ''}
              onChange={(e) => handleDisabledMessageChange(e.target.value)}
              className="w-full h-24 px-3 py-2 bg-secondary rounded-lg text-[15px] leading-[20px] resize-none focus:outline-none"
            />
            <button
              onClick={handleSaveDisabledMessage}
              disabled={savingDisabledMessage}
              className="w-full mt-3 py-2.5 bg-primary text-primary-foreground rounded-xl text-[15px] leading-[20px] font-semibold disabled:opacity-50"
            >
              {savingDisabledMessage ? 'Saving...' : 'Save Message'}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Selector */}
      <div className="flex mx-4 mt-6 p-1 bg-secondary rounded-lg">
        {(['overview', 'photos', 'tasks', 'users'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 py-2 text-[15px] leading-[20px] font-medium rounded-md transition-colors',
              activeTab === tab
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground'
            )}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'overview' && <OverviewTab analytics={analytics} />}
        {activeTab === 'photos' && <PhotosTab />}
        {activeTab === 'tasks' && <TasksTab />}
        {activeTab === 'users' && <UsersTab />}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState<(User & { photoCount: number })[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    const interval = setInterval(loadUsers, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getAdminUsers();
      setUsers(data.users);
      setOnlineUsers(data.onlineUsers);
      setTotal(data.total);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user: User & { photoCount: number }) => {
    const photoMessage = user.photoCount > 0
      ? ` This will also delete ${user.photoCount} photo${user.photoCount === 1 ? '' : 's'} from the gallery.`
      : '';

    if (!confirm(`Delete ${user.username}?${photoMessage}`)) return;

    setDeletingUserId(user.id);
    try {
      await deleteAdminUser(user.id);
      setUsers((currentUsers) => currentUsers.filter((u) => u.id !== user.id));
      setOnlineUsers((currentOnlineUsers) => currentOnlineUsers.filter((u) => u.userId !== user.id));
      setTotal((currentTotal) => Math.max(0, currentTotal - 1));
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeletingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const onlineUserIds = new Set(onlineUsers.map((user) => user.userId));

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card rounded-xl p-4">
        <h3 className="text-[17px] leading-[22px] font-semibold mb-3">
          Online Now ({onlineUsers.length})
        </h3>
        {onlineUsers.length === 0 ? (
          <p className="text-[15px] leading-[20px] text-muted-foreground">
            No users online
          </p>
        ) : (
          <div className="space-y-2">
            {onlineUsers.map((user) => (
              <div key={user.userId} className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Circle className="w-2.5 h-2.5 fill-green-500 text-green-500 shrink-0" />
                  <span className="text-[15px] leading-[20px] font-medium truncate">
                    {user.username}
                  </span>
                </div>
                <span className="text-[12px] leading-[16px] text-muted-foreground shrink-0">
                  {new Date(user.lastSeenAt).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-xl p-4">
        <h3 className="text-[17px] leading-[22px] font-semibold mb-3">
          All Users ({total})
        </h3>
        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {onlineUserIds.has(user.id) && (
                    <Circle className="w-2.5 h-2.5 fill-green-500 text-green-500 shrink-0" />
                  )}
                  <p className="text-[15px] leading-[20px] font-medium truncate">
                    {user.username}
                  </p>
                </div>
                <p className="text-[12px] leading-[16px] text-muted-foreground">
                  Joined {parseTimestamp(user.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className="text-[15px] leading-[20px] font-semibold">
                    {user.photoCount}
                  </p>
                  <p className="text-[12px] leading-[16px] text-muted-foreground">
                    Photos
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteUser(user)}
                  disabled={deletingUserId === user.id}
                  className="p-2 text-destructive disabled:opacity-50"
                  aria-label={`Delete ${user.username}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Overview Tab
function OverviewTab({ analytics }: { analytics: Analytics | null }) {
  if (!analytics) return null;

  return (
    <div className="px-4 space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="👥" label="Total Guests" value={analytics.overview.totalUsers} />
        <StatCard icon="📸" label="Photos" value={analytics.overview.totalPhotos} />
        <StatCard icon="❤️" label="Likes" value={analytics.overview.totalLikes} />
        <StatCard
          icon="✅"
          label="Tasks"
          value={`${analytics.overview.activeTasksCount}/${analytics.overview.totalTasks}`}
        />
      </div>

      {/* Today's Activity */}
      <div className="bg-card rounded-xl p-4">
        <h3 className="text-[17px] leading-[22px] font-semibold mb-3">Today</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-[17px] leading-[22px]">
            <span className="text-muted-foreground">New guests</span>
            <span className="font-medium">{analytics.recent.usersToday}</span>
          </div>
          <div className="flex justify-between text-[17px] leading-[22px]">
            <span className="text-muted-foreground">Photos taken</span>
            <span className="font-medium">{analytics.recent.photosToday}</span>
          </div>
          <div className="flex justify-between text-[17px] leading-[22px]">
            <span className="text-muted-foreground">Likes given</span>
            <span className="font-medium">{analytics.recent.likesToday}</span>
          </div>
        </div>
      </div>

      {/* Top Photos */}
      {analytics.topPhotos.length > 0 && (
        <div className="bg-card rounded-xl p-4">
          <h3 className="text-[17px] leading-[22px] font-semibold mb-3">Top Photos</h3>
          <div className="grid grid-cols-3 gap-2">
            {analytics.topPhotos.slice(0, 6).map((photo) => (
              <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                <img
                  src={photo.thumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[12px] px-1.5 py-0.5 rounded">
                  ❤️ {photo.likeCount}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: string; label: string; value: number | string }) {
  return (
    <div className="bg-card rounded-xl p-4">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-[22px] leading-[28px] font-bold">{value}</div>
      <div className="text-[12px] leading-[16px] text-muted-foreground">{label}</div>
    </div>
  );
}

// Photos Tab
function PhotosTab() {
  const [photos, setPhotos] = useState<AdminPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<AdminPhoto | null>(null);

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const data = await getAdminPhotos('newest', 50);
      setPhotos(data.photos);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;
    
    setDeleting(photoId);
    try {
      await deleteAdminPhoto(photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Error deleting photo:', error);
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (photo: AdminPhoto) => {
    try {
      const response = await fetch(photo.originalUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading photo:', error);
    }
  };

  const handleDownloadAll = async () => {
    if (!confirm(`Download all ${photos.length} photos? This may take a while.`)) return;
    
    for (const photo of photos) {
      await handleDownload(photo);
      // Small delay to prevent overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="px-4">
      {/* Download All Button */}
      {photos.length > 0 && (
        <button
          onClick={handleDownloadAll}
          className="w-full py-3 mb-4 bg-primary text-primary-foreground rounded-xl text-[17px] leading-[22px] font-semibold ios-pressable"
        >
          Download All Photos ({photos.length})
        </button>
      )}

      <div className="grid grid-cols-3 gap-1">
        {photos.map((photo) => (
          <div 
            key={photo.id} 
            className="relative aspect-square group cursor-pointer"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={photo.thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(photo.id);
              }}
              disabled={deleting === photo.id}
              className="absolute top-1 right-1 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {deleting === photo.id ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div 
            className="bg-card rounded-xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPhoto.originalUrl}
              alt=""
              className="w-full aspect-square object-contain bg-black"
            />
            <div className="p-4">
              <p className="text-[17px] font-semibold">{selectedPhoto.username}</p>
              <p className="text-[15px] text-muted-foreground">{selectedPhoto.taskDescription}</p>

              <div className="mt-4">
                <h4 className="text-[15px] leading-[20px] font-semibold mb-2">Reactions</h4>
                {selectedPhoto.reactionDetails.length === 0 ? (
                  <p className="text-[14px] leading-[20px] text-muted-foreground">
                    No reactions yet
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedPhoto.reactionDetails.map((reaction) => (
                      <div
                        key={`${reaction.userId}-${reaction.emoji}-${reaction.createdAt}`}
                        className="flex items-center justify-between gap-3 text-[14px] leading-[20px]"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-lg">{reaction.emoji}</span>
                          <span className="font-medium truncate">{reaction.username}</span>
                        </div>
                        <span className="text-[12px] leading-[16px] text-muted-foreground shrink-0">
                          {parseTimestamp(reaction.createdAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleDownload(selectedPhoto)}
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-[17px] font-semibold"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(selectedPhoto.id)}
                  disabled={deleting === selectedPhoto.id}
                  className="py-3 px-6 bg-destructive text-destructive-foreground rounded-xl text-[17px] font-semibold"
                >
                  {deleting === selectedPhoto.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Tasks Tab
function TasksTab() {
  const [tasks, setTasks] = useState<TaskWithCreator[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await getAdminTasks();
      setTasks(data.tasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (task: TaskWithCreator) => {
    try {
      await updateAdminTask(task.id, { isActive: !task.isActive });
      setTasks(tasks.map(t =>
        t.id === task.id ? { ...t, isActive: !t.isActive } : t
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleStartEdit = (task: TaskWithCreator) => {
    setShowAddTask(false);
    setEditingTaskId(task.id);
    setEditingDescription(task.description);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditingDescription('');
  };

  const handleSaveTask = async (task: TaskWithCreator) => {
    const description = editingDescription.trim();

    if (description.length < 10 || description === task.description) {
      if (description === task.description) {
        handleCancelEdit();
      }
      return;
    }

    setSavingTaskId(task.id);
    try {
      const data = await updateAdminTask(task.id, { description });
      setTasks(tasks.map(t =>
        t.id === task.id ? { ...t, ...data.task } : t
      ));
      handleCancelEdit();
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskDescription.trim() || newTaskDescription.length < 10) return;

    setCreating(true);
    try {
      const data = await createAdminTask(newTaskDescription);
      setTasks([data.task as TaskWithCreator, ...tasks]);
      setNewTaskDescription('');
      setShowAddTask(false);
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      await deleteAdminTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="px-4">
      {/* Add Task Button */}
      <button
        onClick={() => {
          handleCancelEdit();
          setShowAddTask(true);
        }}
        className={cn(
          'w-full py-3 mb-4 bg-primary text-primary-foreground rounded-xl',
          'text-[17px] leading-[22px] font-semibold ios-pressable',
          'flex items-center justify-center gap-2'
        )}
      >
        <Plus className="w-5 h-5" />
        Add New Task
      </button>

      {/* Add Task Form */}
      {showAddTask && (
        <div className="mb-4 bg-card rounded-xl p-4">
          <textarea
            value={newTaskDescription}
            onChange={(e) => setNewTaskDescription(e.target.value)}
            placeholder="Enter task description (min 10 characters)"
            className="w-full h-24 px-3 py-2 bg-secondary rounded-lg text-[17px] placeholder:text-muted-foreground resize-none focus:outline-none"
          />
          <div className="flex gap-3 mt-3">
            <button
              onClick={() => setShowAddTask(false)}
              className="flex-1 py-2 bg-secondary text-foreground rounded-lg text-[17px]"
            >
              Cancel
            </button>
            <button
              onClick={handleAddTask}
              disabled={creating || newTaskDescription.length < 10}
              className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-[17px] font-semibold disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-2">
        {tasks.map((task) => (
          <div key={task.id} className="bg-card rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {editingTaskId === task.id ? (
                  <textarea
                    value={editingDescription}
                    onChange={(e) => setEditingDescription(e.target.value)}
                    className="w-full min-h-24 px-3 py-2 bg-secondary rounded-lg text-[17px] leading-[22px] placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={200}
                    disabled={savingTaskId === task.id}
                    autoFocus
                  />
                ) : (
                  <p className={cn(
                    'text-[17px] leading-[22px] font-medium',
                    !task.isActive && 'text-muted-foreground'
                  )}>
                    {task.description}
                  </p>
                )}
                <div className="flex gap-3 mt-1 text-[12px] leading-[16px] text-muted-foreground">
                  <span>✅ {task.timesCompleted}</span>
                  <span>⏭️ {task.timesSkipped}</span>
                  {editingTaskId === task.id && (
                    <span>{editingDescription.length}/200</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {editingTaskId === task.id ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      disabled={savingTaskId === task.id}
                      className="p-2 text-muted-foreground disabled:opacity-50"
                      aria-label="Cancel edit"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleSaveTask(task)}
                      disabled={savingTaskId === task.id || editingDescription.trim().length < 10}
                      className="p-2 text-primary disabled:opacity-50"
                      aria-label="Save task"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleStartEdit(task)}
                      className="p-2 text-primary"
                      aria-label="Edit task"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <IOSToggle
                      checked={task.isActive ?? false}
                      onChange={() => handleToggleActive(task)}
                    />
                    <button
                      onClick={() => handleDeleteTask(task.id)}
                      className="p-2 text-destructive"
                      aria-label="Delete task"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
